/**
 * Conversões de data do calendário de broadcasts.
 *
 * O banco grava em UTC e os cards da tela exibem em Brasília. O calendário tem
 * que concordar com os cards: se o dia fosse calculado no fuso do navegador,
 * um usuário fora do país veria o grupo num dia e a hora do card em outro.
 * Por isso o fuso é fixo, não `local`.
 */

export const BRT_TZ = 'America/Sao_Paulo';

/**
 * Brasília está em UTC-3 o ano inteiro — o horário de verão acabou em 2019
 * (Decreto 9.772). Se voltar, isto aqui é o ponto único a mudar.
 */
const OFFSET_BRT = '-03:00';

const doisDigitos = (n: number) => String(n).padStart(2, '0');

/** Chave YYYY-MM-DD do dia em Brasília a que um instante UTC pertence. */
export const diaBRT = (isoUtc?: string | null): string | undefined => {
    if (!isoUtc) return undefined;
    const d = new Date(isoUtc);
    if (isNaN(d.getTime())) return undefined;
    // en-CA formata como YYYY-MM-DD, que é exatamente a chave que queremos.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: BRT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
};

/**
 * Instante UTC da borda de um dia de Brasília.
 *
 * Monta a string com o offset explícito em vez de usar o fuso do navegador:
 * `new Date('2026-09-01T00:00:00-03:00')` dá o mesmo resultado em qualquer
 * máquina, o que `new Date(ano, mes, dia)` não dá.
 */
export const limiteUtcDoDiaBRT = (dia: Date, borda: 'inicio' | 'fim'): string => {
    const data = `${dia.getFullYear()}-${doisDigitos(dia.getMonth() + 1)}-${doisDigitos(dia.getDate())}`;
    const hora = borda === 'inicio' ? '00:00:00' : '23:59:59';
    return new Date(`${data}T${hora}${OFFSET_BRT}`).toISOString().slice(0, 19).replace('T', ' ');
};

/** Domingo que abre a grade do mês (pode cair no mês anterior). */
export const inicioDaGrade = (mes: Date): Date => {
    const primeiro = new Date(mes.getFullYear(), mes.getMonth(), 1);
    primeiro.setDate(primeiro.getDate() - primeiro.getDay());
    return primeiro;
};

/** Sábado que fecha a grade do mês (pode cair no mês seguinte). */
export const fimDaGrade = (mes: Date): Date => {
    const ultimo = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    ultimo.setDate(ultimo.getDate() + (6 - ultimo.getDay()));
    return ultimo;
};

export const mesPorExtenso = (mes: Date): string => {
    const texto = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(mes);
    return texto.charAt(0).toUpperCase() + texto.slice(1);
};

export const diaPorExtenso = (chave: string): string => {
    const [ano, mes, dia] = chave.split('-').map(Number);
    const d = new Date(ano, mes - 1, dia);
    const texto = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(d);
    return texto.charAt(0).toUpperCase() + texto.slice(1);
};
